import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Paper,
  Typography,
  Tooltip,
  Button,
  Stack
} from '@mui/material';
import {
  CheckCircleOutline,
  RadioButtonUnchecked,
  Info
} from '@mui/icons-material';

export interface Integration {
  id: string;
  label: string;
  description: string;
  devices: string[];
}

export interface IntegrationSelectorProps {
  integrations: Integration[];
  selectedIntegrations: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}

const IntegrationSelector: React.FC<IntegrationSelectorProps> = ({
  integrations,
  selectedIntegrations,
  onChange,
  disabled = false
}) => {
  const handleToggle = (integrationId: string) => {
    if (disabled) return;
    
    const isSelected = selectedIntegrations.includes(integrationId);
    if (isSelected) {
      onChange(selectedIntegrations.filter(id => id !== integrationId));
    } else {
      onChange([...selectedIntegrations, integrationId]);
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onChange(integrations.map(integration => integration.id));
  };

  const handleClearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  const isAllSelected = selectedIntegrations.length === integrations.length;
  const isNoneSelected = selectedIntegrations.length === 0;

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">
            集成选择
          </Typography>
          <Tooltip title="选择要执行的POS集成类型。未选中的集成将被跳过。">
            <Info fontSize="small" color="action" />
          </Tooltip>
        </Box>
        
        <Stack direction="row" spacing={1}>
          <Button 
            size="small" 
            onClick={handleSelectAll}
            disabled={disabled || isAllSelected}
            variant="outlined"
          >
            全选
          </Button>
          <Button 
            size="small" 
            onClick={handleClearAll}
            disabled={disabled || isNoneSelected}
            variant="outlined"
            color="secondary"
          >
            清空
          </Button>
        </Stack>
      </Stack>

      <Box mb={2}>
        <Chip 
          label={`已选择: ${selectedIntegrations.length} / ${integrations.length}`}
          color={selectedIntegrations.length > 0 ? "primary" : "default"}
          size="small"
        />
      </Box>

      <FormControl component="fieldset" variant="standard" fullWidth disabled={disabled}>
        <FormGroup>
          {integrations.map((integration) => {
            const isSelected = selectedIntegrations.includes(integration.id);
            
            return (
              <Tooltip 
                key={integration.id}
                title={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{integration.label}</Typography>
                    <Typography variant="caption">{integration.description}</Typography>
                    <Typography variant="caption" display="block" mt={0.5}>
                      设备: {integration.devices.join(', ')}
                    </Typography>
                  </Box>
                }
                placement="right"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggle(integration.id)}
                      icon={<RadioButtonUnchecked />}
                      checkedIcon={<CheckCircleOutline />}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>{integration.label}</Typography>
                      <Chip
                        label={integration.id}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    backgroundColor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                />
              </Tooltip>
            );
          })}
        </FormGroup>
      </FormControl>

      {isNoneSelected && (
        <Box mt={2} p={2} bgcolor="warning.light" borderRadius={1}>
          <Typography variant="body2" color="warning.dark">
            ⚠️ 未选择任何集成，测试将不会执行任何设备。
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default IntegrationSelector;

